// MapImportProgress — `/dashboard/map-imports/:id`.
//
// Polls /v1/map-imports/:id every 1.5s while the pipeline is running.
// Progress bar percent + step list + status message are all driven by
// backend writes — never fake-incremented. When status reaches
// `ready_for_review` the user can jump into FloorLayoutEditor for
// manual correction; `failed` shows the friendly error + retry; the
// page is fully resumable (refresh works, the next poll restores the
// current stage).
//
// Spec: docs/specs/floor-plan-import.md §8.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle2, AlertCircle, Loader2, RefreshCw, X,
  ChevronRight, Image as ImageIcon, FileText, Eraser,
} from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import Skeleton from "../components/Skeleton.js";
import {
  getMapImport, retryMapImport, cancelMapImport, approveMapImport,
  artifactUrl, friendlyMapImportError,
  fetchCleanedMeta, uploadCleanedImage,
  type MapImportJobDto,
} from "../lib/mapImportApi.js";
import {
  MAP_IMPORT_STEPS, statusOrdinal,
  type MapImportStatus,
} from "@nexus/shared/map-import";
import { useDashboardSession } from "../auth/useDashboardSession.js";

const POLL_MS = 1500;
// Statuses where the pipeline is still moving forward — keeps polling.
const ACTIVE_STATUSES: ReadonlySet<MapImportStatus> = new Set<MapImportStatus>([
  "created", "uploaded", "queued",
  "preprocessing_started", "image_normalized",
  "furniture_cleanup_started", "furniture_cleanup_completed",
  "binarization_started", "binarization_completed",
  "line_cleanup_started", "line_cleanup_completed",
  "structure_extraction_started",
  "wall_detection_completed", "door_detection_completed", "room_segmentation_completed",
  "vectorization_started", "vectorization_completed",
  "scene_graph_generated",
]);

type Stage =
  | { status: "loading" }
  | { status: "ready"; job: MapImportJobDto }
  | { status: "error"; message: string };

type PreviewTab = "source" | "cleaned" | "svg";

/**
 * Tracks the browser-side cleaned-image synthesis flow that runs once
 * the pipeline reaches `ready_for_review`:
 *
 *   1. fetch cleaned-meta.json (bbox list from OpenAI vision)
 *   2. fetch source artifact as a blob
 *   3. paint white rectangles over each bbox on a <canvas>
 *   4. PNG-encode + POST /v1/map-imports/:id/cleaned-image
 *
 * On success the row's `has_cleaned` flips true and the cleaned tab
 * unlocks. On failure the user sees a retry button — we never silently
 * swallow the error.
 */
type SynthesisState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "done"; r2_key: string }
  | { kind: "failed"; message: string };

export default function MapImportProgress(): React.ReactElement {
  const { id = "" } = useParams<{ id: string }>();
  const nav = useNavigate();
  const session = useDashboardSession();
  // super_admin impersonating into another tenant (demo / support) needs
  // `?tenant_id=` on every request — Rbac.resolveTenant on the worker
  // side won't fall back to the actor's own tenant when an explicit
  // tenant context is required.
  const tenantId = session.acting_tenant_id ?? session.tenant_id ?? null;
  const [stage, setStage] = useState<Stage>({ status: "loading" });
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [previewTab, setPreviewTab] = useState<PreviewTab>("source");
  const [synthesis, setSynthesis] = useState<SynthesisState>({ kind: "idle" });
  // Guard against StrictMode double-invoke + the polling loop re-triggering
  // synth after each refresh. Reset to false in the manual-retry handler.
  const synthRanRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const job = await getMapImport(id, tenantId);
      setStage({ status: "ready", job });
    } catch (err) {
      setStage({ status: "error", message: (err as Error).message ?? "請求失敗" });
    }
  }, [id, tenantId]);

  // Initial fetch
  useEffect(() => { void load(); }, [load]);

  // Polling loop while the job is in an active state. Stops on
  // ready_for_review / approved / saved / failed / cancelled so we
  // don't keep hammering the API for completed jobs.
  useEffect(() => {
    if (stage.status !== "ready") return;
    if (!ACTIVE_STATUSES.has(stage.job.status)) return;
    const timer = window.setTimeout(() => { void load(); }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [stage, load]);

  // ── Cleaned-image canvas synthesis ───────────────────────────────────
  //
  // Cloudflare Workers don't ship native image manipulation, so the
  // map-import pipeline emits cleaned-meta.json (bbox list) but defers
  // the actual PNG composition to the browser. Once the pipeline reaches
  // ready_for_review and the row doesn't yet have `has_cleaned`, we
  // pull source.png + cleaned-meta.json, draw white rectangles over each
  // bbox on a hidden <canvas>, encode as PNG, and POST it back.
  //
  // EVERY async step is wrapped — fetch failures, image decode errors,
  // canvas.toBlob returning null, and upload rejections all funnel to
  // setSynthesis({kind:"failed"}). No unhandled rejection can take down
  // the progress page.
  const synthesizeCleanedImage = useCallback(async (job: MapImportJobDto): Promise<void> => {
    setSynthesis({ kind: "running" });
    let blobUrl: string | null = null;
    try {
      // 1. Fetch the bbox list. May return 404 if the pipeline ran a
      //    very old worker and skipped Phase 2 — surface that distinctly
      //    so the operator knows it's not a transient failure.
      const meta = await fetchCleanedMeta(job.id, tenantId);
      if (!Array.isArray(meta.bboxes)) {
        throw new Error("cleaned-meta.json missing bboxes array");
      }

      // 2. Fetch the source image as a blob. Cookie-auth via credentials.
      const sourceUrl = artifactUrl(job.id, "source", tenantId);
      const sourceResp = await fetch(sourceUrl, { credentials: "include" });
      if (!sourceResp.ok) throw new Error(`fetch source failed: HTTP ${sourceResp.status}`);
      const sourceBlob = await sourceResp.blob();

      blobUrl = URL.createObjectURL(sourceBlob);
      const img = await loadImage(blobUrl);

      // 3. Render at the SOURCE'S natural pixel size so bbox coordinates
      //    (in source-image px) line up exactly.
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("2d canvas context unavailable");

      // 4. Paint source + opaque white rectangles. Clamp each bbox to
      //    image bounds — the vision model occasionally emits 1–2 px
      //    overflows, and a negative-coord fillRect is a silent no-op.
      ctx.drawImage(img, 0, 0);
      ctx.fillStyle = "#ffffff";
      const W = canvas.width;
      const H = canvas.height;
      for (const b of meta.bboxes) {
        const x = Math.max(0, Math.min(W, Math.round(b.x)));
        const y = Math.max(0, Math.min(H, Math.round(b.y)));
        const w = Math.max(0, Math.min(W - x, Math.round(b.w)));
        const h = Math.max(0, Math.min(H - y, Math.round(b.h)));
        if (w > 0 && h > 0) ctx.fillRect(x, y, w, h);
      }

      // 5. PNG-encode + multipart upload back to the API.
      const pngBlob = await canvasToBlob(canvas, "image/png");
      const r = await uploadCleanedImage(job.id, pngBlob, tenantId);

      setSynthesis({ kind: "done", r2_key: r.r2_key });
      await load();                  // refetch so has_cleaned flips true
      setPreviewTab("cleaned");      // jump to the new tab so the user sees the result
    } catch (err) {
      const msg = (err as Error).message ?? String(err);
      setSynthesis({ kind: "failed", message: msg });
      // Intentional: error stays in the UI; we don't toast/alert because
      // the inline retry button next to the cleaned tab is the real
      // affordance. Log so a CSP-allowed console.error reaches debug.
      console.error("cleaned_image_synthesis_failed", msg);
    } finally {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    }
  }, [load]);

  // Trigger synthesis once the pipeline first reaches ready_for_review
  // and the row hasn't been stamped with a cleaned image yet. The
  // synthRanRef guard makes this idempotent across StrictMode double-
  // invoke + polling-driven re-renders.
  useEffect(() => {
    if (stage.status !== "ready") return;
    const j = stage.job;
    if (j.status !== "ready_for_review") return;
    if (j.has_cleaned) return;
    if (synthRanRef.current) return;
    synthRanRef.current = true;
    void synthesizeCleanedImage(j);
  }, [stage, synthesizeCleanedImage]);

  const onRetrySynthesis = () => {
    if (stage.status !== "ready") return;
    synthRanRef.current = false;
    void synthesizeCleanedImage(stage.job);
  };

  const onRetry = async () => {
    setBusyAction("retry");
    try { await retryMapImport(id, tenantId); await load(); }
    catch (err) { alert((err as Error).message); }
    finally { setBusyAction(null); }
  };
  const onCancel = async () => {
    if (!window.confirm("確定要取消這個轉換 job？取消後無法復原，需要重新上傳。")) return;
    setBusyAction("cancel");
    try { await cancelMapImport(id, tenantId); await load(); }
    catch (err) { alert((err as Error).message); }
    finally { setBusyAction(null); }
  };
  // Two action buttons on the ready_for_review action row both commit the
  // pipeline's scene graph into `map_layout_versions` via approveMapImport
  // — the only difference is where the operator lands afterwards.
  //
  //   - 進入人工校正  → editor at the floor's URL (continue refining)
  //   - 儲存為樓層地圖 → case list (operator is done)
  //
  // Originally 進入人工校正 just navigated without calling approve, which
  // meant `map_layout_versions` never got the new row and the editor
  // loaded an empty DropZone. Both buttons commit now; approval is
  // idempotent against the job's `ready_for_review` state, so re-clicking
  // doesn't double-write.
  const commitAndNav = async (destination: "editor" | "sites") => {
    setBusyAction(destination === "editor" ? "enter_editor" : "approve");
    try {
      const r = await approveMapImport(id, tenantId);
      if (destination === "editor" && r.site_id && r.building_id) {
        nav(`/dashboard/sites/${encodeURIComponent(r.site_id)}/buildings/${encodeURIComponent(r.building_id)}/floors/${encodeURIComponent(r.floor_id)}/layout`);
      } else if (destination === "sites") {
        nav("/dashboard/sites");
      } else {
        // Editor was requested but the job is missing site/building (legacy
        // or partial bind); fall back to sites list so the operator can
        // navigate manually. Layout is committed either way.
        nav("/dashboard/sites");
      }
    } catch (err) {
      alert((err as Error).message);
      await load();
    } finally { setBusyAction(null); }
  };
  const onEnterEditor = () => commitAndNav("editor");
  const onApprove = () => commitAndNav("sites");

  if (stage.status === "loading") {
    return (
      <>
        <PageHeader title="平面圖轉換中" description="正在讀取轉換任務狀態…" />
        <Skeleton className="h-40 w-full" />
      </>
    );
  }
  if (stage.status === "error") {
    return (
      <>
        <PageHeader title="平面圖轉換" />
        <div className="flex items-start gap-2 rounded-lg border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>無法載入轉換任務：{stage.message}</span>
        </div>
      </>
    );
  }

  const job = stage.job;
  const isFailed = job.status === "failed";
  const isCancelled = job.status === "cancelled";
  const isReady = job.status === "ready_for_review";
  const isSaved = job.status === "saved" || job.status === "approved";
  const isActive = ACTIVE_STATUSES.has(job.status);

  return (
    <>
      <PageHeader
        title={titleForStatus(job.status)}
        description={subtitleForStatus(job)}
        actions={
          <button className="btn-secondary text-sm" onClick={() => void load()} disabled={busyAction !== null}>
            <RefreshCw className={`h-4 w-4 ${isActive ? "animate-spin" : ""}`} /> 重新整理
          </button>
        }
      />

      {/* Progress bar — backend-driven, no fake increment. */}
      <div className="card p-4 sm:p-5 mb-4">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm font-medium text-text-primary">整體進度</span>
          <span className="text-2xl font-mono text-accent">{job.progress_percent}%</span>
        </div>
        <div className="h-2 w-full rounded-full bg-bg-raised overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              isFailed ? "bg-danger" : isCancelled ? "bg-text-muted" : "bg-accent"
            }`}
            style={{ width: `${job.progress_percent}%` }}
          />
        </div>
        {job.current_message && (
          <p className="mt-3 text-xs text-text-secondary leading-relaxed">
            {isActive && <Loader2 className="inline h-3 w-3 mr-1 animate-spin text-accent" />}
            {job.current_message}
          </p>
        )}
      </div>

      {/* Step list — pending / processing / completed / failed per step. */}
      <div className="card p-4 sm:p-5 mb-4">
        <h2 className="text-sm font-semibold text-text-primary mb-3">詳細步驟</h2>
        <ol className="space-y-2">
          {MAP_IMPORT_STEPS.map((step) => {
            const completionOrd = statusOrdinal(step.completes_at);
            const currentOrd = statusOrdinal(job.status);
            const isStepComplete = currentOrd >= completionOrd && !isFailed && !isCancelled;
            const isStepFailedHere = isFailed && currentOrd < completionOrd;
            const isStepProcessing = !isStepComplete && !isStepFailedHere && currentOrd === completionOrd - 1;
            return (
              <li key={step.key} className="flex items-center gap-3 text-sm">
                <StepIcon state={
                  isStepFailedHere ? "failed"
                    : isStepComplete ? "completed"
                    : isStepProcessing ? "processing"
                    : "pending"
                } />
                <span className={`flex-1 ${isStepComplete ? "text-text-secondary" : "text-text-primary"}`}>{step.label}</span>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Previews — three-tab switcher (原圖 / 清圖 / 場域圖). The cleaned
          tab is populated by browser-side canvas synthesis once the
          pipeline reaches ready_for_review; see synthesizeCleanedImage
          above. Tabs become enabled as their artifact becomes available;
          aria-disabled + aria-label spell out the gating reason for
          screen readers. */}
      <div className="card p-4 sm:p-5 mb-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h2 className="text-sm font-semibold text-text-primary">預覽</h2>
          <div role="tablist" aria-label="預覽切換" className="flex gap-1 text-xs">
            <PreviewTabButton
              active={previewTab === "source"}
              disabled={false}
              onClick={() => setPreviewTab("source")}
              label="原始圖"
            />
            <PreviewTabButton
              active={previewTab === "cleaned"}
              disabled={!canShowCleanedTab(job)}
              disabledReason={cleanedTabDisabledReason(job)}
              onClick={() => setPreviewTab("cleaned")}
              label="清圖"
              indicator={
                synthesis.kind === "running" ? "running"
                  : synthesis.kind === "failed" ? "failed"
                  : job.has_cleaned ? "done"
                  : null
              }
            />
            <PreviewTabButton
              active={previewTab === "svg"}
              disabled={!job.has_svg}
              disabledReason={job.has_svg ? null : "場域圖尚未產生"}
              onClick={() => setPreviewTab("svg")}
              label="場域圖（SVG）"
            />
          </div>
        </div>
        <PreviewPaneSingle tab={previewTab} job={job} synthesis={synthesis} tenantId={tenantId} />
        {synthesis.kind === "failed" && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-2.5 text-xs text-text-secondary">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-warning" />
            <div className="flex-1">
              <p className="text-warning font-medium mb-0.5">清圖合成失敗</p>
              <p className="leading-relaxed">{synthesis.message}</p>
            </div>
            <button
              className="btn-secondary text-xs flex-shrink-0"
              onClick={onRetrySynthesis}
            >
              <Eraser className="h-3.5 w-3.5" /> 重新合成
            </button>
          </div>
        )}
      </div>

      {/* Warnings */}
      {job.warning_messages.length > 0 && (
        <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 mb-4">
          <p className="text-xs font-semibold text-warning mb-1">轉換警告</p>
          <ul className="text-xs text-text-secondary space-y-0.5">
            {job.warning_messages.map((w, i) => <li key={i}>• {w}</li>)}
          </ul>
        </div>
      )}

      {/* Failure */}
      {isFailed && (
        <div className="rounded-lg border border-danger/40 bg-danger/10 p-4 mb-4">
          <div className="flex items-start gap-2 mb-2">
            <AlertCircle className="h-5 w-5 text-danger mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-danger">轉換失敗</p>
              <p className="text-xs text-text-secondary mt-1">{friendlyMapImportError(job.error_code, job.error_message)}</p>
              {job.error_code && <p className="mt-2 text-2xs font-mono text-text-muted">錯誤代碼：{job.error_code}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Cancelled */}
      {isCancelled && (
        <div className="rounded-lg border border-border bg-bg-raised p-4 mb-4 text-sm text-text-secondary">
          這個 job 已被取消。要重新開始請從上傳頁建立新 job。
        </div>
      )}

      {/* Action row */}
      <div className="flex flex-wrap gap-2 justify-end">
        {isActive && (
          <button className="btn-secondary text-sm text-danger" onClick={onCancel} disabled={busyAction !== null}>
            <X className="h-4 w-4" /> 取消
          </button>
        )}
        {(isFailed || isCancelled) && (
          <>
            <button
              className="btn-secondary text-sm"
              onClick={() => {
                // Upload UI lives inside FloorLayoutEditor — go back there
                // if the job was bound to a floor, else to the sites list.
                if (job.site_id && job.building_id && job.floor_id) {
                  nav(`/dashboard/sites/${encodeURIComponent(job.site_id)}/buildings/${encodeURIComponent(job.building_id)}/floors/${encodeURIComponent(job.floor_id)}/layout`);
                } else {
                  nav("/dashboard/sites");
                }
              }}
            >返回樓層編輯器</button>
            <button className="btn-primary text-sm" onClick={onRetry} disabled={busyAction !== null}>
              {busyAction === "retry" ? <><Loader2 className="h-4 w-4 animate-spin" /> 重試中…</> : "重試"}
            </button>
          </>
        )}
        {isReady && (
          <>
            <button className="btn-secondary text-sm" onClick={onRetry} disabled={busyAction !== null}>重新轉換</button>
            <button
              className="btn-secondary text-sm"
              onClick={onEnterEditor}
              disabled={busyAction !== null || !job.floor_id}
              title={job.floor_id ? "" : "尚未綁定樓層，無法進入校正"}
            >
              {busyAction === "enter_editor"
                ? <><Loader2 className="h-4 w-4 animate-spin" /> 寫入中…</>
                : <>進入人工校正 <ChevronRight className="h-4 w-4" /></>}
            </button>
            <button className="btn-primary text-sm" onClick={onApprove} disabled={busyAction !== null}>
              {busyAction === "approve" ? <><Loader2 className="h-4 w-4 animate-spin" /> 儲存中…</> : "儲存為樓層地圖"}
            </button>
          </>
        )}
        {isSaved && (
          <button className="btn-secondary text-sm" onClick={() => nav("/dashboard/sites")}>回到案場列表</button>
        )}
      </div>
    </>
  );
}

function titleForStatus(s: MapImportStatus): string {
  if (s === "ready_for_review") return "轉換完成，等候人工校正";
  if (s === "saved" || s === "approved") return "已儲存為樓層地圖";
  if (s === "failed") return "平面圖轉換失敗";
  if (s === "cancelled") return "轉換已取消";
  return "平面圖轉換中";
}

function subtitleForStatus(j: MapImportJobDto): string {
  if (j.status === "ready_for_review") return "建議進入人工校正調整牆 / 房 / 門位置後，再儲存為正式樓層地圖。";
  if (j.status === "saved" || j.status === "approved") return "轉換結果已寫入樓層地圖。";
  if (j.status === "failed") return j.error_message ?? "請查看下方錯誤訊息並重試。";
  return "整個流程約 30–90 秒。可保留此頁面也可離開，之後再回來查看進度。";
}

function StepIcon({ state }: { state: "pending" | "processing" | "completed" | "failed" }): React.ReactElement {
  if (state === "completed") return <CheckCircle2 className="h-4 w-4 text-success" />;
  if (state === "processing") return <Loader2 className="h-4 w-4 text-accent animate-spin" />;
  if (state === "failed") return <AlertCircle className="h-4 w-4 text-danger" />;
  return <span className="inline-block h-4 w-4 rounded-full border border-border" />;
}

// ── Preview tab + pane helpers ────────────────────────────────────────

function PreviewTabButton(props: {
  label: string;
  active: boolean;
  disabled: boolean;
  disabledReason?: string | null;
  onClick: () => void;
  /** Optional state indicator next to the label (running spinner, done
   *  check, failed warning). Only used by the 清圖 tab today. */
  indicator?: "running" | "done" | "failed" | null;
}): React.ReactElement {
  const { label, active, disabled, disabledReason, onClick, indicator } = props;
  const cls = active
    ? "px-3 py-1.5 rounded-md bg-accent/15 text-accent font-medium border border-accent/30"
    : disabled
      ? "px-3 py-1.5 rounded-md text-text-muted border border-transparent cursor-not-allowed"
      : "px-3 py-1.5 rounded-md text-text-secondary border border-transparent hover:bg-bg-raised";
  return (
    <button
      role="tab"
      aria-selected={active}
      aria-disabled={disabled || undefined}
      aria-label={disabled && disabledReason ? `${label}（${disabledReason}）` : label}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={cls}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        {indicator === "running" && <Loader2 className="h-3 w-3 animate-spin" />}
        {indicator === "done" && <CheckCircle2 className="h-3 w-3 text-success" />}
        {indicator === "failed" && <AlertCircle className="h-3 w-3 text-warning" />}
      </span>
    </button>
  );
}

/** Pre-condition: the cleaned tab is selectable iff the pipeline has
 *  passed furniture_cleanup_completed (so cleaned-meta exists for
 *  synthesis) — even if synthesis hasn't run yet, the user can preview
 *  the placeholder. We DON'T gate on has_cleaned alone because the
 *  whole point of the tab is to host the synthesis spinner / failure
 *  retry while has_cleaned is still false. */
function canShowCleanedTab(job: MapImportJobDto): boolean {
  return statusOrdinal(job.status) >= statusOrdinal("furniture_cleanup_completed");
}

function cleanedTabDisabledReason(job: MapImportJobDto): string | null {
  if (canShowCleanedTab(job)) return null;
  return "等家具偵測完成後才會出現";
}

function PreviewPaneSingle(props: {
  tab: PreviewTab;
  job: MapImportJobDto;
  synthesis: SynthesisState;
  tenantId: string | null;
}): React.ReactElement {
  const { tab, job, synthesis, tenantId } = props;
  if (tab === "source") {
    return (
      <PreviewFrame
        icon={<ImageIcon className="h-3.5 w-3.5" />}
        title="原始平面圖"
        url={job.has_source ? artifactUrl(job.id, "source", tenantId) : null}
        placeholder="上傳中…"
      />
    );
  }
  if (tab === "cleaned") {
    if (job.has_cleaned) {
      return (
        <PreviewFrame
          icon={<Eraser className="h-3.5 w-3.5" />}
          title="清圖（家具 / 標註已蓋白）"
          url={artifactUrl(job.id, "cleaned", tenantId)}
          placeholder="清圖載入中…"
        />
      );
    }
    // No cleaned PNG yet — render the synthesis state directly so the
    // user can see exactly why the tab is empty.
    let placeholder: string;
    if (synthesis.kind === "running") placeholder = "正在前端合成清圖…";
    else if (synthesis.kind === "failed") placeholder = "合成失敗，可在下方重試";
    else if (statusOrdinal(job.status) < statusOrdinal("ready_for_review")) placeholder = "等 AI 跑完整條 pipeline 才會自動合成";
    else placeholder = "準備合成清圖…";
    return (
      <PreviewFrame
        icon={<Eraser className="h-3.5 w-3.5" />}
        title="清圖（家具 / 標註已蓋白）"
        url={null}
        placeholder={placeholder}
        spinner={synthesis.kind === "running"}
      />
    );
  }
  // svg
  return (
    <PreviewFrame
      icon={<FileText className="h-3.5 w-3.5" />}
      title="場域圖（可編輯 SVG）"
      url={job.has_svg ? artifactUrl(job.id, "svg", tenantId) : null}
      placeholder="尚未產生"
    />
  );
}

function PreviewFrame(props: {
  icon: React.ReactNode;
  title: string;
  url: string | null;
  placeholder: string;
  spinner?: boolean;
}): React.ReactElement {
  return (
    <div className="rounded-lg border border-border bg-bg-raised overflow-hidden">
      <div className="px-3 py-2 border-b border-border text-2xs uppercase tracking-wider text-text-muted flex items-center gap-1">
        {props.icon}
        {props.title}
      </div>
      <div className="aspect-[4/3] bg-bg-surface flex items-center justify-center">
        {props.url ? (
          <img src={props.url} alt={props.title} className="max-h-full max-w-full object-contain" />
        ) : (
          <p className="text-2xs text-text-muted inline-flex items-center gap-1.5">
            {props.spinner && <Loader2 className="h-3 w-3 animate-spin" />}
            {props.placeholder}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Canvas synthesis helpers ──────────────────────────────────────────

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = url;
  });
}

function canvasToBlob(c: HTMLCanvasElement, type: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    c.toBlob((b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))), type);
  });
}
